import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class EeBriaOrder extends ModelObject<EeBriaOrder> {

    /**
    * The EeBria order Id of this order.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public eeBriaOrderId: string = "";
    /**
    * The master order Id for this order. This is the ID that customers will knowthe order as.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public masterOrderId: string = "";
    /**
    * The date of this order, provided by EeBria as a Unix Timestamp
    */
    public orderTime: Instant = Instant.ofEpochMilli(0);
    /**
    * The time that this order was last updated on EeBria
    */
    public orderLastUpdatedAt: Instant = Instant.ofEpochMilli(0);
    /**
    * This is the fee percentage of the order, should there be no further alterations to the order fee. This does not reflect any adjustments to the order which may be provided in the producer's payment run file.
    */
    public basicFeePercentage: number = 0;
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.eeBriaOrderId = jsonSerializedObject.eeBriaOrderId;
          this.masterOrderId = jsonSerializedObject.masterOrderId;
          this.orderTime = Instant.parse(jsonSerializedObject.orderTime);
          this.orderLastUpdatedAt = Instant.parse(jsonSerializedObject.orderLastUpdatedAt);
          this.basicFeePercentage = jsonSerializedObject.basicFeePercentage;
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.eeBriaOrderId = this.eeBriaOrderId;
        toRet.masterOrderId = this.masterOrderId;
        toRet.orderTime = this.orderTime.toString();
        toRet.orderLastUpdatedAt = this.orderLastUpdatedAt.toString();
        toRet.basicFeePercentage = this.basicFeePercentage;
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.errorMessage = this.errorMessage;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "EeBriaOrder.BrewmanOrderId cannot be empty"},
    ];
}


export class EeBriaOrderFilter extends ModelObject<EeBriaOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _eeBriaOrderIds: readonly (string)[] | null = null;
    /**
    * Limit to specified eebria order ids
    */
    public get eeBriaOrderIds(): readonly (string)[] | null { return this._eeBriaOrderIds }
    public set eeBriaOrderIds(value: readonly (string)[] | null) {
      this._eeBriaOrderIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.eeBriaOrderIds = jsonSerializedObject.eeBriaOrderIds == null ? null : jsonSerializedObject.eeBriaOrderIds.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.eeBriaOrderIds = this.eeBriaOrderIds == null ? null : this.eeBriaOrderIds.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredEeBriaOrdersResult extends ModelObject<FilteredEeBriaOrdersResult> {

    public matchingCount: number = 0;
    public orders: (EeBriaOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.orders = jsonSerializedObject.orders.map((v: any) => new EeBriaOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.orders = this.orders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "orders", rule: (v: any) => !!v || "orders is required"},
            {field: "orders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as EeBriaOrder[]).reduce((acc: Array<string>, curr: EeBriaOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class EeBriaOrderService extends ServiceBase {

  public async getEebriaOrdersByFilter(filter: EeBriaOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredEeBriaOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredEeBriaOrdersResult(response.results);;
    return this.makeJsonRequest("eeBriaOrder/v1/getEebriaOrdersByFilter", req, false, extractor, opts);
  };
}

export const eeBriaOrderService = new EeBriaOrderService();